import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "#root/routes/constants";
import { showToast } from "#root/components/GlobalToaster";
import { useCreateTenant, Base64UploadFileDto } from "#root/queries/tenants";
import { FILE_KINDS, FILE_REFERENCE_TYPES } from "#root/utils/media";
import { fileToBase64 } from "#root/services/utils";
import { Button, Field, Input, Radio, RadioGroup, Spinner, Text } from "@fluentui/react-components";
import ButtonGroup from "#root/components/ButtonGroup";
import OrganizationForm, { API_SETTING_LABELS, ApiSettingKey, DEFAULT_API_SETTINGS, OrganizationFormData } from "../OrganizationForm";
import './styles.scss';
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { VALIDATION_MESSAGES, VALIDATION_PATTERNS } from "#root/utils/validation";
import { AxiosError } from "axios";
import { TENANT_TYPE } from "#root/utils/tenants";

interface AdminFormData {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    phoneNumber: string;
}

const AddOrganization: React.FC = () => {
    const navigate = useNavigate();
    const createTenant = useCreateTenant();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [passwordManual, setPasswordManual] = useState(true);
    
    const [organizationData, setOrganizationData] = useState<OrganizationFormData>({
        organizationName: "",
        organizationId: "",
        organizationLogo: undefined,
        organizationType: TENANT_TYPE.RAPID_REUNIFICATION,
        apiSettings: DEFAULT_API_SETTINGS
    });
    
    const [adminData, setAdminData] = useState<AdminFormData>({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phoneNumber: ""
    });

    const handleOrganizationSubmit = async (data: OrganizationFormData) => {
        setOrganizationData(data);
        setCurrentStep(1);
    };

    const handleAdminSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentStep(2);
    };

    const handleAdminDataChange = (field: keyof AdminFormData, value: string) => {
        setAdminData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePasswordModeChange = (value: boolean) => {
        setPasswordManual(value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        let logoData: Base64UploadFileDto | undefined;

        try {
            if (organizationData.organizationLogo) {
                const base64Content = await fileToBase64(organizationData.organizationLogo);
                logoData = {
                    name: organizationData.organizationLogo.name,
                    type: organizationData.organizationLogo.type,
                    content: base64Content,
                    referenceId: '', // This will be set by the backend
                    referenceType: FILE_REFERENCE_TYPES.TENANT,
                    kind: FILE_KINDS.LOGO
                };
            }

            // Create a promise that resolves after 10 seconds
            const timeoutPromise = new Promise((resolve) => {
                setTimeout(resolve, 10000);
            });

            // Race between the mutation and the timeout
            await Promise.race([
                createTenant.mutateAsync({
                    tenant: {
                        organizationName: organizationData.organizationName,
                        organizationId: organizationData.organizationId,
                        type: organizationData.organizationType,
                        metroPcrVictims: organizationData.apiSettings.metroPcrVictims,
                        metroPcrContacts: organizationData.apiSettings.metroPcrContacts,
                        webEocVictims: organizationData.apiSettings.webEocVictims
                    },
                    logo: logoData,
                    admin: {
                        firstName: adminData.firstName,
                        lastName: adminData.lastName,
                        email: adminData.email,
                        password: passwordManual ? adminData.password : undefined,
                        phoneNumber: adminData.phoneNumber
                    }
                }),
                timeoutPromise
            ]);

            showToast("Organization creation is in progress", "success");
            navigate(ROUTES.ROOT);
        } catch (error) {
            if (error instanceof AxiosError) 
            {
                const errorMessage = error.response?.data?.Message || "Failed to create organization";
                const cleanedMessage = errorMessage.replace(/\s*\([^)]*\)/g, '');
                showToast(cleanedMessage, "error");
            }
            else
            {
                showToast("Failed to create organization", "error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <OrganizationForm
                        initialData={organizationData}
                        onSubmit={handleOrganizationSubmit}
                        submitButtonText="Next Step"
                        isSubmitting={isSubmitting}
                    />
                );
            case 1:
                return (
                    <form className="form-step" onSubmit={handleAdminSubmit}>
                        <h3>Admin Information</h3>
                        <div className="field-two-columns">
                            <Field label="First Name" required>
                                <Input
                                    value={adminData.firstName}
                                    onChange={(e) => handleAdminDataChange("firstName", e.target.value)}
                                />
                            </Field>
                            <Field label="Last Name" required>
                                <Input
                                    value={adminData.lastName}
                                    onChange={(e) => handleAdminDataChange("lastName", e.target.value)}
                                />
                            </Field>
                        </div>
                        <Field label="Email" required>
                            <Input
                                type="email"
                                value={adminData.email}
                                onChange={(e) => handleAdminDataChange("email", e.target.value)}
                            />
                        </Field>
                        <Field label="Password" required hint={passwordManual ? VALIDATION_MESSAGES.PASSWORD : ""}>
                            <ButtonGroup
                                name="password-mode"
                                options={[
                                    { label: 'Manual', value: true },
                                    { label: 'Random', value: false }
                                ]}
                                onChange={handlePasswordModeChange}
                                value={passwordManual}
                                required
                            />
                            {passwordManual && (
                                <>
                                    <Input className="password-input"
                                        autoComplete="new-password"
                                        name="new-password"
                                        value={adminData.password}
                                        onChange={(e) => handleAdminDataChange("password", e.target.value)}
                                        pattern={VALIDATION_PATTERNS.PASSWORD}
                                        title={VALIDATION_MESSAGES.PASSWORD}
                                    />
                                </>
                            )}
                        </Field>
                        <Field label="Phone Number">
                            <Input
                                value={adminData.phoneNumber}
                                onChange={(e) => handleAdminDataChange("phoneNumber", e.target.value)}
                            />
                        </Field>

                        <div className="form-actions">
                            <Button
                                type="submit"
                                appearance="primary"
                                disabled={isSubmitting}
                            >
                                Create Organization
                            </Button>
                            <Button appearance="secondary" size="large" onClick={() => setCurrentStep(0)} disabled={isSubmitting}>
                                Previous Step
                            </Button>
                            <Button appearance="secondary" size="large" onClick={() => navigate(ROUTES.ROOT)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                );
            case 2:
                return (
                    <form className="form-step" onSubmit={handleSubmit}>
                        <h3>Organization Information</h3>

                        <div className="field-two-columns">
                            <Field label="Organization Name">
                                <Input
                                    value={organizationData.organizationName}
                                    readOnly
                                />
                            </Field>
                            <Field label="Organization ID">
                                <Input
                                    value={organizationData.organizationId}
                                    readOnly
                                />
                            </Field>
                        </div>

                        <Field label="App Settings">
                            <RadioGroup layout="horizontal"
                                disabled
                                value={organizationData.organizationType}>
                                <Radio value={TENANT_TYPE.RAPID_REUNIFICATION} label="Rapid Reunification" />
                                <Radio value={TENANT_TYPE.MERRGE_211} label="MERRGE™ 211" />
                            </RadioGroup>
                        </Field>

                        {organizationData.organizationType !== TENANT_TYPE.MERRGE_211 && (
                            <Field label="API Settings">
                                <div className="api-settings">
                                    {(Object.keys(API_SETTING_LABELS) as ApiSettingKey[]).map(key => (
                                        <div key={key}>
                                            {API_SETTING_LABELS[key]}: {organizationData.apiSettings[key] ? "On" : "Off"}
                                        </div>
                                    ))}
                                </div>
                            </Field>
                        )}

                        {organizationData.organizationLogo && (
                            <Field label="Organization Logo">
                                <img 
                                    src={URL.createObjectURL(organizationData.organizationLogo)} 
                                    alt="Organization Logo" 
                                    className="image-preview"
                                    onLoad={(e) => {
                                        // Clean up the temporary URL after the image loads
                                        URL.revokeObjectURL((e.target as HTMLImageElement).src);
                                    }} 
                                />
                            </Field>
                        )}

                        <h3>Admin Information</h3>

                        <div className="field-two-columns">
                            <Field label="First Name">
                                <Input
                                    value={adminData.firstName}
                                    readOnly
                                />
                            </Field>
                            <Field label="Last Name">
                                <Input
                                    value={adminData.lastName}
                                    readOnly
                                />
                            </Field>
                        </div>

                        <Field label="Email">
                            <Input
                                value={adminData.email}
                                readOnly
                            />
                        </Field>

                        {passwordManual && (
                            <Field label="Password">
                                <Input
                                    value={adminData.password}
                                    readOnly
                                />
                            </Field>
                        )}

                        <Field label="Phone Number">
                            <Input
                                value={adminData.phoneNumber}
                                readOnly
                            />
                        </Field>

                        <div className="warning-text">Creating a new organization will take a few minutes. Are you sure you want to proceed?</div>

                        <div className="form-actions">
                            <Button
                                type="submit"
                                appearance="primary"
                                size="large"
                                disabled={isSubmitting}
                            >
                                Confirm and Create New Organization
                            </Button>
                            <Button appearance="secondary" size="large" onClick={() => setCurrentStep(1)} disabled={isSubmitting}>
                                Previous Step
                            </Button>
                            <Button appearance="secondary" size="large" onClick={() => navigate(ROUTES.ROOT)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="add-organization">
                <div className="emc-page-header">
                    <div className="emc-page-header__top-row">
                        <div className="emc-page-header__breadcrumbs">
                            <Breadcrumbs />
                        </div>
                    </div>
                    <div className="emc-page-header__title">
                        <h2>New Organization <div className="divider"></div></h2>
                    </div>
                </div>

                {renderStep()}
            </div>
            {isSubmitting && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <Spinner size="large" />
                        <Text>Creating organization...</Text>
                    </div>
                </div>
            )}
        </>
    );
};

export default AddOrganization;